import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlContainer, FormGroupDirective } from '@angular/forms';

import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';
import { merge } from 'lodash-es';

import { Dropdown } from 'primeng/dropdown';
import { MultiSelect } from 'primeng/multiselect';

import { DropDownList, MatchTypes, PopupLovColumnConfig } from '@core/interfaces';
import { randomId } from '@util/random-id';

import { columnsDefaultConfig } from './popup-lov.columns';

@Component({
  selector: 'z-popup-lov',
  templateUrl: './popup-lov.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
})
export class PopupLovComponent implements OnInit, OnDestroy, AfterViewInit {
  destroy$ = new Subject<boolean>();

  @Input() fieldName: string;

  @Input() readonly: boolean = false;
  @Input() disabled: boolean = false;
  @Input() uppercase: boolean = false;
  @Input() numeric: boolean = false;
  @Input() currency: boolean = false;
  @Input() decimal: boolean = false;
  @Input() allowedCharacters: string[] = [];

  @Input() clearButton: boolean = true;
  @Input() manualInput: boolean = true;

  @Input() label: string;
  @Input() hint: string;
  @Input() placeholder: string;

  @Input() matchTypes: boolean = false;
  @Input() defaultMatchType: MatchTypes = 'equals';

  @Input() displayValidationErrors: boolean = true;
  @Input() hideLabel: boolean = false;

  @Input() styleClass: string = null;
  @Input() virtualScroll: boolean = false;

  // dropdown properties
  @Input() selectedItemLabelColumn: string = 'column1';
  @Input() selectedItemDescriptionColumn: string = 'column2';
  @Input() selectedItemShowDescription: boolean = true;

  @Input() list: DropDownList;
  @Input() enableDropdown: boolean = false;

  @Input() dropdownPanelMinHeight: number = 60;
  @Input() dropdownPanelMaxHeight: number = 400;
  @Input() dropdownItemSize: number = 32;

  @Input() dropdownPanelMinWidth: number;
  @Input() showDropdownFilter: boolean = false;
  @Input() dropdownFilterBy: string = 'column1,column2';

  @Input() dropdownOptionValueColumn: string = 'column1';
  @Input() dropdownOptionLabelColumn: string = 'column2';

  @Input() doubleClickToOpenPopup: boolean = true;

  @Input() columnsConfig: PopupLovColumnConfig = {};

  @Input() loading: boolean = false;

  @Input() doubleClickAction: () => void = null;
  @Input() buttonAction: () => void = null;

  // multiSelect list properties
  @Input() allowMultipleValues: boolean = false;
  @Input() showToggleAll: boolean = true;
  @Input() selectionLimit: number = null;

  @Output() change = new EventEmitter<string>();

  @Output() dropdownChange = new EventEmitter<string>();
  @Output() dropdownSelectedOptionChange = new EventEmitter<string>();

  @Output() dropdownClick = new EventEmitter<any>();
  @Output() dropdownFocus = new EventEmitter<any>();
  @Output() dropdownBlur = new EventEmitter<any>();
  @Output() dropdownPanelShow = new EventEmitter<any>();
  @Output() dropdownPanelHide = new EventEmitter<any>();
  @Output() dropdownFilter = new EventEmitter<any>();
  @Output() clear = new EventEmitter<any>();
  @Output() multiSelectPanelShow = new EventEmitter<any>();
  @Output() multiSelectPanelHide = new EventEmitter<any>();
  @Output() filter = new EventEmitter<any>();
  @Output() filterMultiSelect = new EventEmitter<any>();

  @Output() matchTypeChange = new EventEmitter<any>();

  @ViewChild('input') input: ElementRef<HTMLInputElement>;
  @ViewChild('dropdown') dropdown: Dropdown;
  @ViewChild('multiSelect') multiSelect: MultiSelect;

  id: string;
  blockId: string;

  dropdownPanelHeight$ = new BehaviorSubject<string>(`${this.dropdownPanelMaxHeight}px`);

  dropdownColumnWidthClass: string[] = [];
  dropdownPanelMinWidthStyle: any = {};

  hasValue$ = new BehaviorSubject<boolean>(false);
  isInvalid$ = new BehaviorSubject<boolean>(false);
  tooltipValue$ = new Subject<any>();

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(private translocoService: TranslocoService, public formGroup: FormGroupDirective) {}

  ngOnInit() {
    this.columnsConfig = merge({}, columnsDefaultConfig, this.columnsConfig);

    this.id = `popup-lov-${this.fieldName}`;
    this.blockId = `${this.id}-${randomId()}`;

    if (this.list?.length > 100) {
      this.virtualScroll = true;
    }

    if (this.doubleClickToOpenPopup && !this.doubleClickAction) {
      this.doubleClickAction = this.buttonAction;
    }

    if (this.doubleClickToOpenPopup) {
      setTimeout(() => {
        if (this.input) {
          this.input.nativeElement.addEventListener('dblclick', (event: any) => {
            this.doubleClickHandler(event);
          });
        }

        if (this.dropdown) {
          this.dropdown.containerViewChild.nativeElement.addEventListener('dblclick', (event: any) => {
            this.doubleClickHandler(event);
          });
        }

        if (this.multiSelect) {
          this.multiSelect.containerViewChild.nativeElement.addEventListener('dblclick', (event: any) => {
            this.doubleClickHandler(event);
          });
        }
      }, 0);
    }

    // starting from 1, not 0 to match how columns are configured
    for (let i = 1; i <= 5; i++) {
      const col = `column${i}`;

      // if column 3 or greater is displayed, give column 2 a width if it doesn't already have one to prevent issues
      if (i >= 3 && this.columnsConfig[col].display && this.columnsConfig.column2.width === 0) {
        this.columnsConfig.column2.width = 200;
      }

      // set classes based on column width
      const width: number = this.columnsConfig[col].width;
      this.columnsConfig[col].class = `fixed-column-${width}`;
    }

    this.dropdownPanelMinWidthStyle = this.dropdownPanelMinWidth ? { minWidth: `${this.dropdownPanelMinWidth}px` } : {};

    if (
      (Array.isArray(this.formGroup.form.get(this.fieldName).value) && this.formGroup.form.get(this.fieldName).value.length === 0) ||
      (!Array.isArray(this.formGroup.form.get(this.fieldName).value) && !this.formGroup.form.get(this.fieldName).value)
    ) {
      this.hasValue$.next(false);
    } else {
      this.hasValue$.next(true);
    }

    this.checkForInvalid();

    this.formGroup.form
      .get(this.fieldName)
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if ((Array.isArray(value) && value.length === 0) || (!Array.isArray(value) && !value)) {
          this.hasValue$.next(false);
        } else {
          this.hasValue$.next(true);
        }

        if (typeof value === 'string' || !value) {
          this.tooltipValue$.next(value);
        } else {
          this.tooltipValue$.next(value.join(', '));
        }

        this.checkForInvalid();
        this.change.emit(value);
      });

    this.formGroup.form
      .get(this.fieldName)
      .statusChanges.pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        if (status === 'DISABLED') {
          this.disabled = true;
        } else {
          this.disabled = false;
        }

        this.checkForInvalid();
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  ngAfterViewInit() {
    if (this.multiSelect && this.multiSelect.value && typeof this.multiSelect.value === 'string') {
      this.multiSelect.value = (this.multiSelect.value as string).split(',');
    }
  }

  checkForInvalid() {
    if (!this.formGroup.form.get(this.fieldName)) {
      this.isInvalid$.next(false);
      return;
    }

    this.isInvalid$.next(!this.formGroup.form.get(this.fieldName).pristine && this.formGroup.form.get(this.fieldName).invalid);
  }

  actionButtonHandler = () => {
    if (this.buttonAction) {
      this.buttonAction();
    }
  };

  doubleClickHandler = (event: MouseEvent) => {
    if (event.detail === 2 && this.doubleClickAction) {
      this.doubleClickAction();
    }
  };

  calculateDropdownPanelSize() {
    let calculatedHeight: number;

    if (!this.enableDropdown) {
      return;
    }

    if (this.multiSelect && this.allowMultipleValues && this.multiSelect.optionsToRender) {
      calculatedHeight = this.multiSelect.optionsToRender.length * this.dropdownItemSize;
    } else if (this.dropdown && !this.allowMultipleValues && this.dropdown.optionsToDisplay) {
      calculatedHeight = this.dropdown.optionsToDisplay.length * this.dropdownItemSize;
    } else {
      return;
    }

    if (calculatedHeight < this.dropdownPanelMaxHeight) {
      if (calculatedHeight > this.dropdownPanelMinHeight) {
        this.dropdownPanelHeight$.next(`${calculatedHeight + 16}px`);
      } else {
        this.dropdownPanelHeight$.next(`${this.dropdownPanelMinHeight}px`);
      }
    } else {
      this.dropdownPanelHeight$.next(`${this.dropdownPanelMaxHeight}px`);
    }
  }

  // event handlers
  onChangeHandler = (event: any) => {
    this.dropdownChange.emit(event.value);
    this.dropdownSelectedOptionChange.emit(this.dropdown.selectedOption);
  };

  onClick = (event: any) => {
    this.dropdownClick.emit(event);
  };

  onFocus = (event: any) => {
    this.dropdownFocus.emit(event);
  };

  onBlur = (event: any) => {
    this.dropdownBlur.emit(event);
  };

  onDropDownPanelShow = (event: any) => {
    this.dropdownPanelShow.emit(event);
    this.calculateDropdownPanelSize();
  };

  onDropDownPanelHide = (event: any) => {
    this.dropdownPanelHide.emit(event);
  };

  onClear = (event: any) => {
    this.clear.emit(event);
  };

  onMultiSelectClear = (event: any) => {
    this.formGroup.form.get(this.fieldName).setValue('');
    this.clear.emit(event);
  };

  onFilterDropdown = (event: any) => {
    this.dropdownFilter.emit(event);
  };

  onChange = (event: any) => {
    this.change.emit(event.value);
  };

  onFilter = (event: any) => {
    this.filter.emit(event);

    this.calculateDropdownPanelSize();
  };

  onFilterMultiSelect = (event: any) => {
    this.filterMultiSelect.emit(event);
    this.onFilter(event);
  };

  onMultiSelectPanelShow = (event: any) => {
    this.multiSelectPanelShow.emit(event);

    this.calculateDropdownPanelSize();
  };

  onMultiSelectPanelHide = (event: any) => {
    this.multiSelectPanelHide.emit(event);
  };

  onMatchTypeChange = (event: any) => {
    this.matchTypeChange.emit(event);
  };
}
